import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Appearance, useColorScheme } from 'react-native';
import { logger } from '../utils/logger';

type Theme = 'light' | 'dark' | 'system';

interface ThemeColors {
  background: string;
  surface: string;
  primary: string;
  text: string;
  textSecondary: string;
  border: string;
  success: string;
  warning: string;
  error: string;
  card: string;
}

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  colors: ThemeColors;
  isDark: boolean;
}

const lightColors: ThemeColors = {
  background: '#F7F7F7',
  surface: '#FFFFFF',
  primary: '#007BFF',
  text: '#2F3E46',
  textSecondary: '#6B7280',
  border: '#E5E7EB',
  success: '#28A745',
  warning: '#FF8C00',
  error: '#DC2626',
  card: '#FFFFFF',
};

const darkColors: ThemeColors = {
  background: '#0F0F0F',
  surface: '#1A1A1A',
  primary: '#007BFF',
  text: '#FFFFFF',
  textSecondary: '#9CA3AF',
  border: '#374151',
  success: '#28A745',
  warning: '#FF8C00',
  error: '#EF4444',
  card: '#1A1A1A',
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const systemColorScheme = useColorScheme();
  const [theme, setThemeState] = useState<Theme>('system');

  useEffect(() => {
    loadTheme();
  }, []);

  const loadTheme = async () => {
    try {
      const storedTheme = await AsyncStorage.getItem('@theme');
      if (storedTheme && ['light', 'dark', 'system'].includes(storedTheme)) {
        setThemeState(storedTheme as Theme);
      }
    } catch (error) {
      logger.error('Error loading theme', { error });
    }
  };

  const setTheme = async (newTheme: Theme) => {
    try {
      setThemeState(newTheme);
      await AsyncStorage.setItem('@theme', newTheme);
    } catch (error) {
      logger.error('Error saving theme', { error, newTheme });
    }
  };

  const isDark = theme === 'system' 
    ? systemColorScheme === 'dark' 
    : theme === 'dark';

  const colors = isDark ? darkColors : lightColors;

  return (
    <ThemeContext.Provider value={{
      theme,
      setTheme,
      colors,
      isDark
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}